import React, { useContext, useState } from 'react';
import CreateDetailsForm from 'src/components/Forms/CreateCase';
import styled from 'styled-components';
import { useAppSelector } from 'src/state/store/hooks';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { ICreateCaseRequest } from 'src/state/slice/models/CaseLifecycleTypes';
import { logger } from 'src/logger';
import { KatAlert, KatButton, KatModal } from '@amzn/katal-react';
import { scrollToElement } from 'src/utilities/PageLayout';
import { DDMAttributeContext } from 'src/integration/ddm/providers/DDMAttributesProvider';
import { AnonymousCreateCase } from 'src/constants/ApiConstants';
import WAFCaptchaContainer from 'src/components/Captcha';
import { callWafFetch } from 'src/utilities/AwsWafApiHelper';
import { AnonymousUserCreateCaseRequestImpl } from 'src/pages/CreateCase/RequestFactoryImpl/AnonymousUserCreateCaseRequest';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { RecognizedUserCreateCaseRequestImpl } from 'src/pages/CreateCase/RequestFactoryImpl/RecognizedUserCreateCaseRequest';
import { fetchCountry } from 'src/utilities/AuthParamHelper';

export const CreateCaseHeaderSection = styled.div`
  padding: 1rem 1.5rem 1rem 1.5rem; /* top right bottom left  */
  background-color: ${({ theme }) => theme.colors.Snow};
`;

export const CreateCaseHeader = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: 1.75;
`;
export const CreateCaseDescription = styled.div`
  font-size: ${({ theme }) => theme.fontSize.m};
  font-weight: ${({ theme }) => theme.fontWeight.normal};
  color: ${({ theme }) => theme.colors.Tarpon};
`;

export const CreateCaseFormWrapper = styled.div`
  padding: 0rem 1.5rem 0rem 1.5rem; /* top right bottom left  */
`;

export const CreateCasePage = styled.div``;

/**
 * Reporting type for create case form.
 */
export enum REPORTING_TYPE {
  ANONYMOUS = 'ANONYMOUS',
  LOGGED_IN = 'LOGGED_IN',
}
export interface ICreateCaseFormWithCaptcha {
  reportingType: REPORTING_TYPE;
  allowAttachments: boolean; //Default: false. Whether form should allow file upload capability or not
  showReferralInfo: boolean; //Default: false. Indicates whether the referral info section in the create case form should be shown or not
  successModalBody: React.ReactElement | string; //captcha success modal based on program
  messages: {
    CREATE_CASE_FAILURE_MESSAGE: string; //Failure message on the UI
    CREATE_CASE_SUCCESSFUL_MODAL_HEADER: string; //Success case, This text is shown in the modal header.
    CREATE_CASE_SUCCESSFUL_MODAL_BUTTON_CLOSE_LABEL: string; //Close label in the case successful captcha scenario
    REPORT_A_CASE_HEADER: string; //Section header for the form.
    REPORT_A_CASE_DESCRIPTION: string; //Section description for the form
    CAPTCHA_TITLE: string; //When submit button on the form is clicked, then captcha is rendered. This title is shown on the popup.
  };
}

export const CreateCaseFormWithCaptcha: React.FC<
  ICreateCaseFormWithCaptcha
> = ({
  messages,
  allowAttachments = false,
  showReferralInfo = false,
  successModalBody,
  reportingType,
}) => {
  const userContext = useContext(AuthContext);
  const ddmAttributes = useContext(DDMAttributeContext);
  const { requestForm, attachments, referralInformation } = useAppSelector(
    (state) => state.createCase,
  );

  const [isError, setError] = useState(false);
  const [isCaseCreationSuccessful, setCaseCreationSuccessful] = useState(false);
  const [isCaptchaVisible, setCaptchaVisiblity] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const { localizationContext } = useLocalizationContext();

  const onCreateCaseClicked = () => {
    setSubmitting(true);
    setCaptchaVisiblity(true);
  };

  const buildCreateCaseRequest = (): ICreateCaseRequest | undefined => {
    if (reportingType === REPORTING_TYPE.LOGGED_IN) {
      const updatedReferralInformation = {
        ...referralInformation,
        country: fetchCountry(
          userContext.preferredAccountId,
          userContext.accountAttributes,
        ),
      };
      const createCaseRequest: ICreateCaseRequest =
        new RecognizedUserCreateCaseRequestImpl(
          userContext,
          requestForm,
          updatedReferralInformation,
          {
            caseLocale: localizationContext.getLocale(),
          },
          attachments,
          ddmAttributes,
        ).generateCreateCaseRequest();
      logger.info(`Creating case with payload. `, { createCaseRequest });
      return createCaseRequest;
    } else {
      const createCaseRequest: ICreateCaseRequest =
        new AnonymousUserCreateCaseRequestImpl(
          userContext,
          requestForm,
          {
            caseLocale: localizationContext.getLocale(),
          },
          attachments,
          ddmAttributes,
        ).generateCreateCaseRequest();
      logger.info(`Creating case with payload. `, { createCaseRequest });
      return createCaseRequest;
    }
  };

  const onCaptchaValidationSuccess = async () => {
    const createCaseRequest = buildCreateCaseRequest();
    callWafFetch(
      userContext.isAnonymousUser,
      AnonymousCreateCase,
      createCaseRequest,
      () => {
        setCaseCreationSuccessful(true);
      },
      () => {
        setError(true);
        scrollToElement('#error-case-creation-component');
      },
    );
    setSubmitting(false);
  };

  const onCaptchaValidationFailure = (error: any) => {
    logger.error(`Failed to validate captcha.`, { error });
  };

  const onCaptchaModalClose = () => {
    setSubmitting(false);
  };

  return (
    <CreateCasePage>
      {isError && (
        <KatAlert
          id="error-case-creation-component"
          variant="danger"
          description={messages.CREATE_CASE_FAILURE_MESSAGE}
        />
      )}

      <CreateCaseHeaderSection>
        <CreateCaseHeader>{messages.REPORT_A_CASE_HEADER}</CreateCaseHeader>
        <CreateCaseDescription>
          {messages.REPORT_A_CASE_DESCRIPTION}
        </CreateCaseDescription>
      </CreateCaseHeaderSection>
      <CreateCaseFormWrapper>
        <CreateDetailsForm
          onFormSubmission={onCreateCaseClicked}
          submitting={isSubmitting}
          showReferralInfo={showReferralInfo}
          allowAttachments={allowAttachments}
        />
        <WAFCaptchaContainer
          captchaTitle={messages.CAPTCHA_TITLE}
          isCaptchaVisible={isCaptchaVisible}
          setCaptchaVisibility={setCaptchaVisiblity}
          onCaptchaValidationSuccess={onCaptchaValidationSuccess}
          onCaptchaValidationFailure={onCaptchaValidationFailure}
          onCaptchaModalClose={onCaptchaModalClose}
        />
        <KatModal
          visible={isCaseCreationSuccessful}
          title={messages.CREATE_CASE_SUCCESSFUL_MODAL_HEADER}
          onClose={() => {
            location.reload();
          }}
          footer={
            <KatButton
              variant="primary"
              onClick={() => {
                location.reload();
              }}
            >
              {messages.CREATE_CASE_SUCCESSFUL_MODAL_BUTTON_CLOSE_LABEL}
            </KatButton>
          }
        >
          {successModalBody}
        </KatModal>
      </CreateCaseFormWrapper>
    </CreateCasePage>
  );
};
